import React, { Suspense, useEffect } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "@reduxjs/toolkit";
import { PropTypes } from "prop-types";
import { useNavigate } from "react-router-dom";
import reactWrapper from "@harbor/elements/utils/react/wrapper";

//reducers
import * as actions from "../actions";

//style
import css from "../overviewMagneticStyle.less";

//component
import AppHOC from "../../../generics/AppHOC";
import DashletTable from "../../../common/DashletTable";
const ErrorComponent = React.lazy(() =>
  import("../../../common/ErrorComponent")
);
import HarborDonut from "../../../common/HbrDonut";
import QoeListComponent from "../../../common/QoeListComponent";
import ChartLegend from "../../../common/ChartLegend";

//utils
import { getThemeColors } from "../../../utils/colors";
import { getDonutData } from "../../../utils/views/overview";
import availTableConfig from "./config/table/siteAvailTableConfig";
import availDonutConfig from "./config/donut/siteAvailDonutConfig";
import {
  getSource,
  columnsName,
  columnsDeviceName,
  getSourceDevice
} from "./config/table/harborTableHeaderConfig";
import deviceTableConfig from "./config/table/deviceTableConfig";
import Spinner from "../../../common/Spinner";
import timeFilterConfig from "../../../generics/config/time-filter-config";
import ApiService from "../../../config/api-config";
import {
  saveSitesDonutData,
  saveSitesDetailsData,
  saveSitesSummaryData,
  saveSitesOverviewTableData,
  getTopSiteListData,
  formatLocationName
} from "../../sites/sitesUtils";
import { exportCsvZip, getReportTimeRangeDate, formatLegendData, downloadAsCSVFile, escapeCSVField } from "../../../utils/common";
import { getSitesOverviewQuery } from "../../../config/reqConfig/overview";
import {
  COLOR,
  defaultCellValue,
  routeURLs,
  timeFilterTypesCustomV4,
} from "../../../utils/enums";

import { shortDisplayDate24HourWithSec, formatDate, displayDateTime } from "../../../utils/displayTime";
import { useI18n } from "../../../i18n/useI18n";
import { formatNumber } from "../../../utils/format";

const mapStateToProps = state => {
  return {
    overview: state.vanalytics.overview
  };
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(actions, dispatch)
});

const [HbrCard, HbrButton, HbrIconButton, HbrTooltip] = reactWrapper([
  "hbr-card",
  "hbr-button",
  "hbr-icon-button",
  "hbr-tooltip"
]);

const DashletSiteView = props => {
  const { tViews } = useI18n('views');
  const navigate = useNavigate();
  let title = (
    <div data-cy="sites-link">
      <div className="header-content" role="heading" aria-level={'2'}>
        {tViews('dashletSiteViewSites')}
      </div>
    </div>
  );
  let topTableConfig = {
    ...availTableConfig,
    title: tViews('topAvailableSites')
  };

  const {
    selectedSite,
    timePeriodSelected,
    siteAPIData,
    deviceSiteOverview,
    deviceSummaryLoader,
    errorFilterViewDevice
  } = props.globalFilter;

  const {
    overviewSiteLoader,
    overviewSiteError,
    overviewSiteDonut,
    overviewSiteStacked
  } = siteAPIData;
  let overviewTopSites;
  if (props.isReporting && siteAPIData?.sitesTableData?.data) {
    overviewTopSites = getTopSiteListData(siteAPIData.sitesTableData.data);
  }

  useEffect(() => {
    updateDashletFilter("avail");

    /*****For Executive Report - Sites Section */
    if (
      props.isReporting &&
      props.executiveSummary.reportData &&
      props.executiveSummary.reportData.length > 0
    ) {
      //  let promisesQueue = [];
      const { overlay_id, time_range, current_period_start , current_period_end} = props.executiveSummary.reportData[0];
      const timeFrame = getReportTimeRangeDate(time_range);
      getSitesOverviewQuery.query.rules[0].value[0] = timeFrame;
      let globalV4PayloadReport = {
        time_frame: timeFilterTypesCustomV4[time_range],
        entry_ts: {
          start: shortDisplayDate24HourWithSec(formatDate(current_period_start).utc()),
          end: shortDisplayDate24HourWithSec(formatDate(current_period_end).utc())
          }
      };
      const defaultRequest = { ...globalV4PayloadReport }
      ApiService.getSitesTableTimeAvailability(
        defaultRequest,
        overlay_id
      ).then(res => {
        if (res && !res.errorObject) {
          saveSitesData(res.data);
        } else {
          props.setOverviewError(res);
        }
        // callback function for executive summary report to identify data is present for sites view widget
        props.isReportSiteViewCallback(true);
      });
    }
  }, []);

  const saveSitesData = res => {
    const overviewDonut = saveSitesDonutData(res.data);
    const overviewStacked = saveSitesDetailsData(res);
    const overviewSummary = saveSitesSummaryData(res.data);
    const sitesOverviewTab = saveSitesOverviewTableData(res.data);

    res &&
      res?.data &&
      res.data.map(
        data =>
          (data.availability === defaultCellValue
            ? (data.availability = defaultCellValue)
            : data.availability) &&
          (data.availability_prev === defaultCellValue
            ? (data.availability_prev = defaultCellValue)
            : data.availability_prev)
      );

    const result = {
      sitesTableData: res,
      overviewSiteDonut: overviewDonut,
      overviewSiteStacked: overviewStacked,
      overviewSiteSummary: overviewSummary,
      sitesOverviewTab: sitesOverviewTab
    };
    props.setSitesTableAvailability(result);
  };

  const updateDashletFilter = view => {
    const { setDashletFilter, setDashletLoading } = props.actions;
    setDashletLoading("SITE");
    setDashletFilter("SITE", { view });
  };

  const getChartTooltipConfig = () => {
    const timefilter = props.globalFilter.timePeriodSelected;
    return {
      labels: [
        {
          key: "current",
          label: tViews('dashletSiteViewAvailability')
        },
        {
          key: "previous",
          label: `${tViews('applicationOverviewLabelAvailabilityOn')} ${timeFilterConfig[timefilter].toolTip}`
        }
      ],
      colors: getThemeColors("overlay"),
      type: "number",
      format: "percent"
    };
  };

  const formatCsvPercent = value =>
    value === defaultCellValue ? defaultCellValue : formatNumber(value, "percent");

  const handleZipCSVExport = () => {
    const donutData = overviewSiteDonut?.summary || {};
    const tableData = formatLocationName(overviewSiteStacked || []);
    const tableColumns = availTableConfig.columns.map(column => ({
      ...column,
      title:
        column.property === "up_percent"
          ? `% ${tViews('allSiteAvailabilityLabel')}`
          : column.title
    }));
    const chartTooltipConfig = getChartTooltipConfig();
    const currentAvailabilityLabel = chartTooltipConfig.labels[0].label;
    const previousAvailabilityLabel = chartTooltipConfig.labels[1].label;
    const fileDateTime = displayDateTime(new Date(), "yyyy-MM-dd hh mm ss aa");
    const tableHeaders = [
      tableColumns[0].title,
      tableColumns[1].title,
      `${tableColumns[1].title} ${tViews("trendingAppsChange")}`,
      tableColumns[2].title,
      `${tableColumns[3].title} - ${currentAvailabilityLabel}`,
      `${tableColumns[3].title} - ${previousAvailabilityLabel}`
    ]

    exportCsvZip({
      zipName: `${tViews("app360Overview")}_${tViews("a11yLabels.app360QoeLabel")}_${fileDateTime}.zip`,
      files: [
        {
          name: `${tViews("a11yLabels.app360QoeLabel")}_${tViews("a11yLabels.donut")}_${tViews("allSitesView.availability")}_${tViews("a11yLabels.data")}_${fileDateTime}.csv`,
          headers: [
            tViews('allSiteAvailabilityLabel'),
            tViews('dashletSiteViewSites')
          ],
          rows: [
            [availDonutConfig.text.good.tooltip, donutData.good || 0],
            [availDonutConfig.text.fair.tooltip, donutData.fair || 0],
            [availDonutConfig.text.bad.tooltip, donutData.bad || 0],
            [availDonutConfig.text.notAvailable.tooltip, donutData.notAvailable || 0]
          ]
        },
        {
          name: `${tViews('siteTableAvailability')}_${tViews("optimization.csvLabels.tableCsvTitle")}_${fileDateTime}.csv`,
          headers: tableHeaders,
          rows: tableData.length
            ? tableData.map(row => [
              row.site_name,
              formatCsvPercent(row.up_percent),
              formatNumber(row.up_percent_change, "changePercent"),
              row.city,
              formatCsvPercent(row.tooltip?.current),
              formatCsvPercent(row.tooltip?.previous)
            ])
            : [Array(tableHeaders.length).fill(tViews('noDataAvailable'))]
        }
      ]
    });
  };

  const deviceRows = getSourceDevice(deviceSiteOverview?.data || []);
  const hasTopDeviceRows = deviceRows.length > 0;
  const handleTopDevicesCSVExport = () => {
    const headers = [
      tViews("applicationOverviewLabelTopDevices"),
      tViews("allSiteAvailabilityLabel"),
      `${tViews("allSiteAvailabilityLabel")} ${tViews("trendingAppsChange")}`,
      tViews("applicationOverviewLabelBandwidthMbps"),
      tViews("applicationOverviewLabelDataUsage")
    ];
    const rows = hasTopDeviceRows
      ? deviceRows.map(row => [
        row.local_host_name,
        `${row.curr_up_percent}%`,
        `${row.change_percentage}%`,
        row.bandwidth,
        row.usage
      ]) : [];

    const csvContent = [
      headers.map(escapeCSVField).join(","),
      ...rows.map(row => row.map(escapeCSVField).join(","))
    ].join("\n");

    const csvTitle = `${tViews("app360Overview")}_${tViews("applicationOverviewLabelTopDevices")}`;

    downloadAsCSVFile(csvContent, csvTitle);
  };

  if (selectedSite > 0) {
    return (
      <HbrCard className={css["site-dashlet-one-device"]} data-cy="sites-device-card" container={true}>
        <div className="site-one-device-container">
          <div className="title-device-dashlet hbr-type-h2">
            {tViews('applicationOverviewLabelTopDevices')}
            {hasTopDeviceRows && (
              <HbrTooltip content={tViews("downloadCSV")} hoist>
                <span>
                  <HbrIconButton
                    size="20px"
                    name="file-csv"
                    onClick={handleTopDevicesCSVExport}
                    arialabel={tViews("downloadCSV")}
                    data-testid="overview-top-devices-csv-btn"
                  />
                </span>
              </HbrTooltip>
            )}
          </div>
          {deviceSummaryLoader ? (
            <div
              className="spinner-component-site-view"
              data-cy="spinner-component-site-view"
            >
              <Spinner />
            </div>
          ) : errorFilterViewDevice ? (
            <Suspense fallback={<Spinner />}>
              <ErrorComponent
                {...errorFilterViewDevice}
                width={"210px"}
                className={"small-dashlet-error"}
              />
            </Suspense>
          ) : (
                <div className="devices-content-block">
                  {deviceSiteOverview?.data ? (
                    <div className="table-view-one-device">
                      <DashletTable
                        config={deviceTableConfig}
                        data={deviceSiteOverview.data}
                        source={getSourceDevice(deviceSiteOverview.data)}
                        columnsName={columnsDeviceName}
                        isTableShown={true}
                        style={{ height: "358px" }}
                      />
                    </div>
                  ) : <Spinner />}
                </div>
              )}
        </div>

      </HbrCard>
    );
  } else {
    //let pieColors = [];
    let donutData = [];
    if (availDonutConfig !== undefined) {
      if (overviewSiteDonut?.summary)
        donutData = getDonutData(
          availDonutConfig.text,
          overviewSiteDonut.summary
        );
    }
    if (props.isReporting) {
      availTableConfig.legend = {
        theme: "overlay",
        labels: [
          tViews('applicationCircuitViewLabelAvailable'),
          tViews('applicationCircuitViewLabelPrevious'),
          tViews('applicationOverviewTotal')
        ]
      }
    }
    const showCsvExport =
      overviewSiteStacked?.length > 0 ||
      Object.values(overviewSiteDonut?.summary || {}).some(Boolean);
    return (
      <HbrCard
      className={`${css[props.isReporting ? "site-dashlet-reporting" : "site-dashlet"]}`}
      data-cy="site-dashlet-view" container={true} data-testid="site-dashlet">
        <div
          className={props.isReporting ? "site-dashlet-view-reporting" : "site-dashlet-view"}
          data-cy="site-view"
        >
          {!props.isReporting &&
            <div
              className={props.isReporting ? "title-block-reporting" : "title-block"}
            >
              <div className="header-content hbr-type-h3">{title}</div>
              <div className="site-view-actions">
                <HbrButton variant="text" onClick={() => navigate(routeURLs.sites)} aria={{label : tViews('a11yLabels.overviewSitesDetails')}}>
                  {tViews('dashletSiteViewDetails')}
                </HbrButton>
                {showCsvExport && (
                  <HbrTooltip content={tViews('a11yLabels.zipDownloadTooltipText')} hoist>
                    <span>
                      <HbrIconButton
                        size="20px"
                        name="file-csv"
                        onClick={handleZipCSVExport}
                        arialabel={tViews('a11yLabels.csvZipDownloadLabel')}
                        data-testid="overview-sites-csv-btn"
                      />
                    </span>
                  </HbrTooltip>
                )}
              </div>
            </div>}
          {overviewSiteLoader ? (
            <div
              className="spinner-component-site-view"
              data-cy="spinner-component-site-view"
            >
              <Spinner />
            </div>
          ) : overviewSiteError ? (
            <Suspense fallback={<Spinner />}>
              <ErrorComponent
                {...overviewSiteError}
                width={"210px"}
                className={"small-dashlet-error"}
              />
            </Suspense>
          ) : (
                <div className="content-block">
                  {overviewSiteLoader ? (
                    <div
                      className="spinner-component-site-view"
                      data-cy="spinner-component-site-view"
                    >
                      <Spinner />
                    </div>
                  ) : (
                      overviewSiteDonut &&
                      availDonutConfig &&
                      overviewSiteDonut.summary && (
                        <>
                          <div
                            className={
                              props.isReporting ? "site-donut-reporting" : "site-donut"
                            }
                          >
                            {props.isReporting && <div
                              className="title-block-reporting hbr-type-h3"
                            >
                              {tViews('siteAvailabilityBreakdown')}
                            </div>}
                            <div className="donut-widget">
                              <HarborDonut
                                data={donutData}
                                donutLabel={tViews('sitesLabel')}
                              />
                              <div className="legend-site-view">
                                <QoeListComponent
                                  overviewPage={true}
                                  iconType={[
                                    COLOR.GOOD,
                                    COLOR.FAIR,
                                    COLOR.POOR,
                                    COLOR.GRAY
                                  ]}
                                  qoeScore={[0, 0, 0, 0]}
                                  qoeLabels={[
                                    availDonutConfig.text.good.title,
                                    availDonutConfig.text.fair.title,
                                    availDonutConfig.text.bad.title,
                                    availDonutConfig.text.notAvailable.title
                                  ]}
                                  tooltipText={tViews('unknownSiteAvailTtip')}
                                  position={3}
                                  ellipsis={true}
                                />
                              </div>
                            </div>
                          </div>
                        </>
                      )
                    )}
                  {props.isReporting && overviewSiteLoader ? (
                    <Spinner />
                  ) : (
                      props.isReporting &&
                      overviewTopSites &&
                      topTableConfig && (
                        <>
                          <div
                            className={
                              props.isReporting
                                ? "table-chartview report-table"
                                : "table-chartview"
                            }
                          >
                            <div className="title-site-dashlet hbr-type-h3">
                              {tViews('topAvailableSites')}
                            </div>
                            <DashletTable
                              config={topTableConfig}
                              data={overviewTopSites}
                              timeFilterType={props.reportLegendText}
                              chartTooltip={true}
                              chartConfig={getChartTooltipConfig()}
                            ></DashletTable>
                          </div>
                          {!props.isReporting && <div className="legend-ctra">
                            <ChartLegend data={formatLegendData(availTableConfig.siteLegend)}></ChartLegend>
                          </div>}
                        </>
                      )
                    )}

                  {overviewSiteLoader ? (
                    <Spinner />
                  ) : (
                      overviewSiteStacked &&
                      availTableConfig && (
                        <div className="table-view">
                          <div
                            className={
                              props.isReporting
                                ? "table-chartview report-table"
                                : "table-chartview"
                            }
                          >
                            <div className="title-site-dashlet hbr-type-h3">
                              {tViews('siteTableAvailability')}
                            </div>
                            <DashletTable
                              config={availTableConfig}
                              data={formatLocationName(overviewSiteStacked)}
                              timeFilterType={
                                props.isReporting
                                  ? props.reportLegendText
                                  : timePeriodSelected
                              }
                              chartTooltip={true}
                              chartConfig={getChartTooltipConfig()}
                              source={getSource([])}
                              columnsName={columnsName()}
                              style={{ height: "40px", width: "380px" }}
                            ></DashletTable>
                          </div>
                          {!props.isReporting &&<div className="legend-ctr">
                            <ChartLegend data={formatLegendData(availTableConfig.siteLegend)}></ChartLegend>
                          </div>}
                        </div>
                      )
                    )}
                </div>
              )}
        </div>
      </HbrCard>
    );
  }
};

DashletSiteView.propTypes = {
  actions: PropTypes.object.isRequired,
  timeFilter: PropTypes.object,
  globalFilter: PropTypes.object.isRequired,
  overview: PropTypes.object,
  isReporting: PropTypes.bool,
  executiveSummary: PropTypes.object,
  setSitesTableAvailability: PropTypes.func,
  getTopSiteListData: PropTypes.func,
  timeRange: PropTypes.any,
  reportLegendText: PropTypes.string,
  setOverviewError: PropTypes.func,
  isReportSiteViewCallback: PropTypes.func
};

export default (
  connect(mapStateToProps, mapDispatchToProps)(AppHOC(DashletSiteView))
);
